import { router } from "@inertiajs/react";
import { AlertTriangle, CheckCircle2, Download, FileText, Save } from "lucide-react";
import { useMemo, useState } from "react";

import LastPurchaseInfo from "@/components/last-purchase-info";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { bulkUpsertPurchaseRequestPurchaseSelectionsPath, exportPurchaseOrdersPurchaseRequestPath, purchaseRequestPath } from "@/routes";
import type { LastPurchaseSummary, PriceComparison, PriceComparisonPrice } from "@/types";

interface Props {
  comparison: PriceComparison;
  purchaseRequestId: number;
  canSaveSelections?: boolean;
  // Optional extra info about the PR to enrich exports
  purchaseRequestInfo?: {
    id: number;
    request_number?: string;
    sector?: { id?: number | null; name?: string | null; code?: string | null } | null;
  };
  // Frontend-only gate: allow exports only if approval flow is completed
  canExport?: boolean;
  onSaved?: () => void;
  lastPurchasesByRequestItemId?: Record<number, LastPurchaseSummary>;
  onOpenHistory?: (requestItemId: number, requestItemDescription: string) => void;
}

const formatCurrency = (amount: number, currency = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(amount);

export default function PurchaseSelectionTable({ comparison, purchaseRequestId, canSaveSelections = true, canExport = false, onSaved, lastPurchasesByRequestItemId = {}, onOpenHistory }: Props) {
  const initial = useMemo(() => {
    const map = new Map<number, number>(); // request_item_id -> quote_item_id
    comparison.rows.forEach((row) => {
      row.prices.forEach((price) => {
        if (price?.is_selected && price.request_item_id && price.quote_item_id) {
          map.set(price.request_item_id, price.quote_item_id);
        }
      });
    });
    return map;
  }, [comparison.rows]);

  const [selection, setSelection] = useState<Map<number, number>>(initial);

  const handleCellClick = (
    requestItemId: number,
    price: PriceComparisonPrice | null | undefined,
  ) => {
    if (!price) return;
    if (price.missing_exchange_rate) return; // Deshabilitar USD sin TC
    if (!price.quote_item_id) return;
    const next = new Map(selection);
    if (selection.get(requestItemId) === price.quote_item_id) {
      next.delete(requestItemId);
    } else {
      next.set(requestItemId, price.quote_item_id);
    }
    setSelection(next);
  };

  const handleSave = () => {
    const selections: { request_item_id: number; quote_item_id: number }[] = [];
    selection.forEach((quote_item_id, request_item_id) => selections.push({ request_item_id, quote_item_id }));

    const removedRequestItemIds: number[] = [];
    initial.forEach((_quoteItemId, requestItemId) => {
      if (!selection.has(requestItemId)) {
        removedRequestItemIds.push(requestItemId);
      }
    });

    const payload: {
      selections: { request_item_id: number; quote_item_id: number }[];
      remove_request_item_ids?: number[];
    } = { selections };

    if (removedRequestItemIds.length > 0) {
      payload.remove_request_item_ids = removedRequestItemIds;
    }

    router.post(
      bulkUpsertPurchaseRequestPurchaseSelectionsPath(purchaseRequestId),
      payload,
      {
        preserveScroll: true,
        onSuccess: () => onSaved?.(),
      },
    );
  };

  const selectedSupplierIds = useMemo(() => {
    const ids = new Set<number>();
    comparison.rows.forEach((row) => {
      const reqId = row.request_item.id;
      const quoteItemId = selection.get(reqId);
      if (!quoteItemId) return;
      // Find the supplier for that quoteItem in this row
      row.prices.forEach((price) => {
        if (price && price.quote_item_id === quoteItemId && price.supplier_id) {
          ids.add(price.supplier_id);
        }
      });
    });
    return Array.from(ids);
  }, [selection, comparison.rows]);

  // Build backend export URL including selected supplier filters
  const jsonExportHref = (() => {
    const base = purchaseRequestPath(purchaseRequestId);
    return `${base}/export_purchase_selection_json`;
  })();

  if (!comparison?.rows.length) return null;

  const { available_request_items = [] } = comparison;
  const requestItemMap = new Map<number, { product_id?: number | null }>(
    available_request_items.map((ri) => [ri.id, { product_id: ri.product_id ?? null }]),
  );

  return (
    <div className="w-full">
      <div className="mb-2 text-sm text-muted-foreground">
        • Haz clic en la celda del proveedor que quieres seleccionar para cada ítem. • Los precios en USD sin tipo de cambio no son seleccionables.
      </div>
      <div className="force-scrollbar-x">
        <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[300px]">Artículo</TableHead>
                <TableHead className="text-center min-w-[100px]">Cantidad</TableHead>
                <TableHead className="text-center min-w-[80px]">Unidad</TableHead>
                {comparison.headers.suppliers.map((s) => (
                  <TableHead key={s.quote_id} className="text-center min-w-[150px]">
                    <div className="space-y-1">
                      <div className="font-medium">{s.name}</div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.rows.map((row) => {
                const selectedQuoteItemId = selection.get(row.request_item.id);
                return (
                  <TableRow key={row.request_item.id}>
                    <TableCell className="font-medium">
                      <div className="space-y-1">
                        <div>{row.request_item.description}</div>
                        <LastPurchaseInfo
                          requestItemId={row.request_item.id}
                          requestItemDescription={row.request_item.description}
                          hasProductAssigned={Boolean(requestItemMap.get(row.request_item.id)?.product_id)}
                          lastPurchase={lastPurchasesByRequestItemId[row.request_item.id]}
                          onOpenHistory={onOpenHistory}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{row.request_item.quantity}</TableCell>
                    <TableCell className="text-center">{row.request_item.unit}</TableCell>
                    {row.prices.map((price, idx) => {
                      const disabled = [
                        price == null,
                        price?.missing_exchange_rate,
                        price?.unit_price_ars === null,
                      ].some(Boolean);
                      const isSelected = price && price.quote_item_id === selectedQuoteItemId;
                      const isBestPrice = !!price?.is_best_price;
                      return (
                        <TableCell
                          key={`c-${row.request_item.id}-${idx}`}
                          onClick={() => !disabled && handleCellClick(row.request_item.id, price)}
                          className={
                            `text-center cursor-${disabled ? "not-allowed" : "pointer"} ` +
                            (isSelected ? "bg-green-50" : disabled ? "opacity-50" : "hover:bg-blue-50")
                          }
                        >
                          {price ? (
                            <div className="space-y-1">
                              <div className="font-medium">
                                {price.unit_price_ars !== null
                                  ? formatCurrency(price.unit_price_ars, "ARS")
                                  : <span className="text-muted-foreground">No disponible</span>}
                              </div>
                              {!price.missing_exchange_rate && price.unit_price_ars !== null && isBestPrice && (
                                <Badge variant="default" className="mt-1 text-xs bg-green-600">
                                  Mejor precio
                                </Badge>
                              )}
                              {price.missing_exchange_rate && (
                                <div className="flex items-center justify-center gap-1 text-amber-700 text-xs">
                                  <AlertTriangle className="h-3 w-3" /> Sin tipo de cambio
                                </div>
                              )}
                              {isSelected && (
                                <div className="flex items-center justify-center gap-1 text-green-700 text-xs">
                                  <CheckCircle2 className="h-3 w-3" /> Seleccionado
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-muted-foreground">-</div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
        </Table>
      </div>
      <div className="pt-4">
        <div className="flex items-center justify-end gap-2">
          {selectedSupplierIds.length > 0 && (
            canExport ? (
              <>
                <Button size="sm" asChild>
                  <a
                    href={exportPurchaseOrdersPurchaseRequestPath(purchaseRequestId, { supplier_ids: selectedSupplierIds })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="h-4 w-4 mr-1" /> Exportar orden(es)
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={jsonExportHref} target="_blank" rel="noopener noreferrer">
                    <FileText className="h-4 w-4 mr-1" /> Exportar JSON
                  </a>
                </Button>
              </>
            ) : (
              <div className="text-sm text-muted-foreground mr-auto">
                Para exportar las órdenes o el JSON de compra, primero debes aprobar la solicitud en el flujo de aprobaciones.
              </div>
            )
          )}
          {canSaveSelections && (
            <Button size="sm" variant={"outline"} onClick={handleSave} className="bg-green-600 hover:bg-green-700 text-white">
              <Save className="h-4 w-4 mr-1" /> Guardar selección
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
